import { Rx, SubtreeController } from "subtree.js/core";
import type { AppDeps } from "./app-deps";
import { loadProfile, signOut } from "./core/ops/auth-ops";
import { setSentryUser } from "./sentry";
import { BroadcastStageController } from "./shell/broadcast-stage-controller";

/**
 * Four states, not two. `profileFailed` is the case a plain
 * authenticated/unauthenticated gate gets wrong: Firebase holds a valid session
 * but `/user/me` did not answer, and sending that user to the login form loops,
 * because signing in again succeeds at Firebase and changes nothing here.
 */
export type Gate = "loading" | "signedOut" | "profileFailed" | "signedIn";

export class AppState {
  readonly gate = new Rx<Gate>("loading");
  /** Signed in at Firebase as a registered account, not an anonymous customer session. */
  readonly registered = new Rx(false);
  readonly profileError = new Rx<string | null>(null);
}

export abstract class AppActions {
  abstract signOut(): Promise<void>;
  /** Re-asks `/user/me` without touching the Firebase session, so a transient outage is recoverable without signing out. */
  abstract retryProfile(): Promise<void>;
}

export type AppControllerDeps = Pick<
  AppDeps,
  | "client"
  | "session"
  | "profileRepository"
  | "storeRepository"
  | "locationsRepository"
  | "pendingCartsRepository"
  | "providersRepository"
  | "capabilitiesRepository"
  | "broadcastStudio"
>;

/** The app root: loads the profile whenever the session opens, forgets everything when it closes, and mirrors the gate for the router. */
export class AppController extends SubtreeController implements AppActions {
  readonly state = new AppState();
  readonly stage: BroadcastStageController;

  constructor(readonly deps: AppControllerDeps) {
    super();
    this.subtree.put(AppState, this.state);
    this.subtree.put(AppActions, this);
    this.stage = this.own(new BroadcastStageController(this.subtree, deps));
    this.sync(() => this.#reconcile(), [deps.session, deps.profileRepository]);
    // A page opened from a magic-link email finishes signing in here; the session then notifies.
    void deps.session.completePendingSignIn().catch((e) => console.error("magic link sign-in failed", e));
  }

  #reconcile(): void {
    const { session, profileRepository } = this.deps;
    const profile = profileRepository.status;
    if (session.stage === "signedIn" && profile.kind === "idle") void loadProfile(this.deps);
    if (session.stage === "signedOut") this.#forget();
    this.state.registered.value = session.stage === "signedIn" && !session.isAnonymous;
    this.state.profileError.value = profile.kind === "failed" ? profile.message : null;
    this.state.gate.value = gateOf(session.stage, profile.kind);
    setSentryUser(profileRepository.profile?.id ?? null);
  }

  #forget(): void {
    const {
      profileRepository,
      storeRepository,
      locationsRepository,
      pendingCartsRepository,
      providersRepository,
      capabilitiesRepository,
    } = this.deps;
    profileRepository.reset();
    storeRepository.reset();
    locationsRepository.reset();
    pendingCartsRepository.reset();
    providersRepository.reset();
    capabilitiesRepository.reset();
  }

  signOut = async () => {
    await signOut(this.deps);
  };

  retryProfile = async () => {
    if (this.deps.session.stage !== "signedIn") return;
    this.deps.profileRepository.reset();
    await loadProfile(this.deps);
  };
}

function gateOf(stage: AppControllerDeps["session"]["stage"], profile: string): Gate {
  if (stage === "restoring") return "loading";
  if (stage === "signedOut") return "signedOut";
  switch (profile) {
    case "loaded":
      return "signedIn";
    case "failed":
      return "profileFailed";
    default:
      return "loading";
  }
}
